/** Gets OAuth URLs for existing connections and creates first-connect URLs on demand. */

import { useMutation, useQuery } from 'react-query';

import { IntegrationType } from 'src/generated/mloop-graphql';

import { ApiVerb, apiPost } from 'src/utils/api';

export interface GetOAuthURLRequest {
  integration_type: IntegrationType;
  redirect_uri?: string;
  login_hint?: string;
  // Optional OAuth scopes to request on top of the provider's required scopes. Each must be one of
  // the provider's declared optional scopes or the backend rejects the request.
  optional_scopes?: string[];
  greenhouse_subdomain?: string;
}

interface GetOAuthURLResponse {
  url?: string;
  redirect_url?: string;
}

async function getIntegrationAuthorizeUrl(data: GetOAuthURLRequest): Promise<GetOAuthURLResponse> {
  const response = await apiPost(ApiVerb.GET_INTEGRATION_AUTHORIZE_URL, data);
  return response.data;
}

/** Prefetches an OAuth URL when the caller can safely create it without more user input. */
export default function useIntegrationAuthorizeUrl(
  data: Omit<GetOAuthURLRequest, 'org_id'>,
  options?: { enabled?: boolean }
) {
  return useQuery(
    [
      'integration_authorize_url',
      data.integration_type,
      data.redirect_uri,
      data.login_hint,
      data.greenhouse_subdomain,
      (data.optional_scopes ?? []).join(','),
    ],
    () => getIntegrationAuthorizeUrl({ ...data }),
    options
  );
}

/** Creates an OAuth URL after the user supplies all required first-connect fields. */
export function useIntegrationAuthorizeUrlMutation() {
  return useMutation((data: Omit<GetOAuthURLRequest, 'org_id'>) => getIntegrationAuthorizeUrl(data));
}
